import { designVariables } from "@studio3t/design-variables";
import {
  GenericDropdown,
  GenericDropdownContext,
  Icon,
  IconSVG,
  type ListItemProps,
  SVGColorKey,
} from "@studio3t/generic-components";
import styled from "styled-components";
import {
  DROPDOWN_TRIGGER_ATTR,
  DropdownOpenReporter,
} from "@/components/dropdownDismissGuard";

const SINGLE_LINE = designVariables.type.singleLine;
const BUTTON = designVariables.ui.button;
const TABLE = designVariables.ui.table;
const TOOLBAR = designVariables.ui.toolbar;

// Toolbar above a table: a flex-grow filter input on the left, dropdown filters
// pushed to the right. Shared by the Pipelines and Services lists.
export const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${TOOLBAR.gap}px;
  padding: ${TABLE.padding}px;
  border-bottom: 1px solid ${(props) => props.theme.tableBackground};
`;

export const FilterInputWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

export const FilterControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${TOOLBAR.gap}px;
  margin-left: auto;
`;

// Secondary-button-style dropdown trigger. Built locally rather than via the
// prebuilt button derivative because Figma uses a small 10px BaseChevron
// (Figma var Button/chevron — not exported by design-variables yet), while
// the derivative renders icons at the fixed 16px button icon size.
export const FilterTrigger = styled.button<{ $active?: boolean }>`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: ${BUTTON.gap}px;
  height: ${BUTTON.s.size}px;
  padding: 0 ${BUTTON.s.paddingw}px;
  border-radius: ${BUTTON.corner}px;
  font-size: ${SINGLE_LINE.small.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.small.medium.fontWeight};
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  color: ${(props) => props.theme.buttonSecondaryDefaultText};
  background: ${(props) =>
    props.$active
      ? props.theme.buttonSecondaryActiveBackground
      : props.theme.buttonSecondaryDefaultBackground};
  border: 1px solid
    ${(props) =>
      props.$active
        ? props.theme.buttonSecondaryActiveBorder
        : props.theme.buttonSecondaryDefaultBorder};
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: ${(props) => props.theme.buttonSecondaryHoverBackground};
    border-color: ${(props) => props.theme.buttonSecondaryHoverBorder};
  }
`;

// Multi-select dropdown filter rendered as a secondary button showing the
// current selection. Selecting none or every option both read as "<label>: All".
export function FilterMultiSelect<T extends string>({
  placeholder,
  values,
  options,
  onChange,
  onOpenChange,
}: {
  placeholder: string;
  values: T[];
  options: { value: T; label: string }[];
  onChange: (values: T[]) => void;
  /** The menu's open state, for a table's dropdownDismissGuard. */
  onOpenChange?: (open: boolean) => void;
}) {
  const selected = options.filter((o) => values.includes(o.value));
  const allSelected =
    selected.length === 0 || selected.length === options.length;
  const display = allSelected
    ? `${placeholder}: All`
    : selected.length === 1
      ? selected[0].label
      : `${placeholder} (${selected.length})`;
  const toggle = (value: T) =>
    onChange(
      values.includes(value)
        ? values.filter((v) => v !== value)
        : [...values, value],
    );
  return (
    <GenericDropdown
      width={{ strategy: "fixed", size: 180 }}
      placement="bottom-end"
      dropdownItems={[
        options.map(
          (o): ListItemProps => ({
            id: o.value,
            type: "checkbox",
            label: o.label,
            value: values.includes(o.value),
            onClick: () => toggle(o.value),
          }),
        ),
      ]}
    >
      <GenericDropdownContext.Consumer>
        {({ open }) => (
          <FilterTrigger
            type="button"
            $active={open}
            {...DROPDOWN_TRIGGER_ATTR}
          >
            <DropdownOpenReporter onOpenChange={onOpenChange} />
            {display}
            <IconSVG
              icon={Icon.BaseChevron}
              size={10}
              svgColorKey={SVGColorKey.semanticTextBody}
            />
          </FilterTrigger>
        )}
      </GenericDropdownContext.Consumer>
    </GenericDropdown>
  );
}
